import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import AppLayout from "@/layouts/AppLayout";
import usePageTitle from "@/hooks/usePageTitle";
import { DOMAIN, PERMISSION, useRequireCapability, useCanManageTasksBool } from "@/hooks/useCapabilities";
import { useNavigate, useParams } from "@/router";
import { CardSkeleton, ProfileSkeleton, SectionHeadingSkeleton } from "@/components/Skeleton";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import Button from "@/components/Button";
import { GET_TASK_QUERY } from "@/features/global/components/TaskSideSheet/queries";
import TaskDetailsHeader from "./_components/TaskDetailsHeader";
import TaskDetailsContent from "./_components/TaskDetailsContent";
import AddTaskReferenceSideSheet from "./_components/AddTaskReferenceSideSheet";

const taskNotFoundTitle = "Task Not Found";
const taskNotFoundMessage =
  "The task you're looking for could not be found. It may not exist or you may not have permission to view it.";

function isTaskNotFoundError(message?: string) {
  return /task not found|inaccessible due to permissions/i.test(message ?? "");
}

export default function TaskDetailPage() {
  useRequireCapability({
    domain: DOMAIN.TASKS,
    permission: PERMISSION.viewTasks
  });
  usePageTitle("/tasks/:taskId", "Task Details");

  const { taskId } = useParams("/tasks/:taskId");
  const navigate = useNavigate();
  const canManageTasks = useCanManageTasksBool();

  const { data, loading, error } = useQuery(GET_TASK_QUERY, {
    variables: { id: taskId },
    skip: !taskId
  });

  const task = data?.task;
  const initialLoading = loading && !task;

  const [addReferenceOpen, setAddReferenceOpen] = useState(false);
  const existingReferenceIds = useMemo(() => (task?.references || []).map((r) => r.id), [task]);

  const returnToTasksAction = (
    <Button variant="filled" onClick={() => navigate("/tasks")} data-testid="return-to-tasks-button">
      Return to Tasks
    </Button>
  );

  const taskNotFoundState = (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <ErrorBoundary
        title={taskNotFoundTitle}
        message={taskNotFoundMessage}
        image="default"
        variant="fullPage"
        action={returnToTasksAction}
      />
    </div>
  );

  return (
    <AppLayout interior>
      {initialLoading ? (
        <>
          <ProfileSkeleton />
          <SectionHeadingSkeleton />
          <CardSkeleton rows={3} />
          <SectionHeadingSkeleton />
          <CardSkeleton rows={3} />
        </>
      ) : error && !task ? (
        isTaskNotFoundError(error.message) ? (
          taskNotFoundState
        ) : (
          <div className="flex items-center justify-center min-h-[60vh] p-6">
            <ErrorBoundary
              title="Unable to Load Task"
              message={error.message}
              image="default"
              variant="fullPage"
              action={returnToTasksAction}
            />
          </div>
        )
      ) : task ? (
        <>
          <TaskDetailsHeader
            task={task}
            onAddReference={canManageTasks ? () => setAddReferenceOpen(true) : undefined}
          />
          <TaskDetailsContent
            task={task}
            refetchQueries={[GET_TASK_QUERY]}
            onAddReference={() => setAddReferenceOpen(true)}
          />
          <AddTaskReferenceSideSheet
            open={addReferenceOpen}
            onClose={() => setAddReferenceOpen(false)}
            taskId={task.id}
            existingReferenceIds={existingReferenceIds}
            refetchQueries={[GET_TASK_QUERY]}
          />
        </>
      ) : (
        taskNotFoundState
      )}
    </AppLayout>
  );
}
